"use client";

import {
  createContext,
  useContext,
  useEffect,
  type PropsWithChildren,
} from "react";
import {
  initAmplitudeClient,
  setAmplitudeStoreId,
  sendExposureEvent,
} from "@/services/analitica";
import { useEffectOnce } from "@/hooks/use-effect-once";
import { Experiments, ExperimentsFlags } from "@/services/amplitude/experiment";

type AnaliticaProviderProps = PropsWithChildren<{
  deviceId?: string;
  experiments: Experiments;
}>;

type AmplitudeContextType = {
  deviceId?: string;
  experiments: Experiments;
};

const AnaliticaContext = createContext<AmplitudeContextType>(
  {} as AmplitudeContextType
);

export const AnaliticaProvider = ({
  children,
  deviceId,
  experiments,
}: AnaliticaProviderProps) => {
  useEffect(() => {
    if (deviceId) {
      initAmplitudeClient({ deviceId });
    }
  }, [deviceId]);

  return (
    <AnaliticaContext.Provider value={{ deviceId, experiments }}>
      {children}
    </AnaliticaContext.Provider>
  );
};

export const useAnalitica = () => {
  return useContext(AnaliticaContext);
};

export const useExperiment = (flagKey: ExperimentsFlags) => {
  const { experiments } = useAnalitica();
  const experiment = experiments[flagKey];

  useEffectOnce(() => {
    if (experiment?.key) {
      sendExposureEvent({ flagKey, variantKey: experiment.key });
    }
  });

  return experiment;
};

export const useSetAmplitudeStoreId = (storeId: string) => {
  setAmplitudeStoreId(storeId);
};
